import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useAuth } from '@/contexts/AuthContext';
import { KeyboardShortcutsHelp } from './KeyboardShortcutsHelp';
import { useQueryClient } from '@tanstack/react-query';

export function GlobalKeyboardShortcuts() {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const queryClient = useQueryClient();
  const [showHelp, setShowHelp] = useState(false);
  const [sequenceKey, setSequenceKey] = useState<string | null>(null);

  // Clear sequence after timeout
  useEffect(() => {
    if (!sequenceKey) return;
    const timeout = setTimeout(() => setSequenceKey(null), 1000);
    return () => clearTimeout(timeout);
  }, [sequenceKey]);

  useKeyboardShortcuts([
    {
      key: '?',
      shift: true,
      callback: () => setShowHelp(true),
      description: 'Show keyboard shortcuts',
    },
    {
      key: 'Escape',
      callback: () => setShowHelp(false),
      description: 'Close dialogs',
    },
    {
      key: 'g',
      callback: () => setSequenceKey('g'),
      description: 'Go to... (press g then another key)',
    },
    {
      key: 'h',
      callback: () => {
        if (sequenceKey === 'g') {
          navigate('/');
          setSequenceKey(null);
        }
      },
      description: 'Go to home (after pressing g)',
    },
    {
      key: 'a',
      callback: () => {
        if (sequenceKey === 'g' && isAdmin) {
          navigate('/admin');
          setSequenceKey(null);
        }
      },
      description: 'Go to admin (after pressing g)',
    },
    {
      key: 'r',
      callback: () => {
        if (user) {
          queryClient.invalidateQueries({ queryKey: ['userData', user.id] });
        }
      },
      description: 'Refresh data',
    },
  ]);

  return (
    <>
      <KeyboardShortcutsHelp open={showHelp} onOpenChange={setShowHelp} />
      {sequenceKey && (
        <div className="fixed bottom-4 left-4 bg-primary text-primary-foreground px-3 py-2 rounded-lg shadow-lg text-sm font-mono animate-in fade-in slide-in-from-bottom-2">
          Press a key: {sequenceKey}...
        </div>
      )}
    </>
  );
}
